// A puzzle, whole: the board to find the answer on, what happened in the
// game, and the controls. The screen supplies the controls that move between
// puzzles (Previous, Skip, Next) and the one that leaves (Menu or Back); the
// panel adds the hint and the line's own controls, and everything to read.

import { useRef, useState, type ReactNode } from "react";
import { PuzzleBoard, usePuzzle } from "../board/PuzzleBoard.tsx";
import type { Snapshot, WrongInfo } from "../board/puzzle.ts";
import type { Epilogue, Refutation, Score } from "../board/lines.ts";
import { reportFault, isSignedOut, sendJson, errorText } from "../app/api.ts";
import { engineLine, scoreOf } from "../app/engine.ts";
import { epdOf, fmtDate, fmtScore, moveLabel, opponentOf, speedLabel, whitePov, type GameInfo } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { HIDDEN_TAGS, KIND, TAG_MEANS, humanTag, kindOf, kindText } from "../app/kinds.ts";
import { prefs, savePrefs } from "../app/prefs.ts";
import { follow, navigate } from "../app/router.ts";
import { toast } from "../app/toast.ts";
import { pgnDate } from "../board/share.ts";
import { BoardMenu, BoardScreen } from "./BoardScreen.tsx";
import { Aftermath, LineMoves, LineRow } from "./LineRow.tsx";
import { OpeningExplain } from "./opening-text.tsx";

/** A puzzle as the server serves it. */
export interface PuzzleData {
  ply: number;
  move_number: number;
  kind?: string;
  fen: string;
  widget_moves: string;
  played_san: string;
  best_san: string;
  best_score: Score;
  played_score: Score;
  difficulty?: number;
  tags?: string[];
  refutations?: Refutation[];
  epilogues?: Epilogue[];
  opening?: any;
}

/** How close to the best book move still counts as theory (OPENINGS.md §3.3). */
const OPENING_ANSWER_TOLERANCE = 0.08;

/** The hint in three presses: which piece moves, then the move, then gone. */
const HINT_STEPS = [
  { label: "Hint", title: "Which piece moves; press again for the move itself" },
  { label: "Show", title: "The move itself; press again to take the hint away" },
  { label: "Hide", title: "Take the hint away" },
] as const;

export function PuzzlePanel({
  puzzle: data,
  game,
  tolerance,
  solved: solvedBefore,
  onSolved,
  lead,
  slots,
  sheetTop,
  sheetBottom,
  showGame,
  repertoireId,
  onTag,
  hidden,
}: {
  puzzle: PuzzleData;
  game: GameInfo;
  tolerance?: number;
  solved: boolean;
  onSolved?: () => void;
  /** The leftmost slot: the way out. */
  lead: ReactNode;
  /** The slots after the hint: moving between puzzles. Told whether the
   *  puzzle is solved, since that changes what moving on is called. */
  slots: (solved: boolean) => ReactNode;
  sheetTop?: ReactNode;
  sheetBottom?: ReactNode;
  showGame?: boolean;
  repertoireId?: number | null;
  onTag?: (tag: string) => void;
  hidden?: boolean;
}) {
  const kind = kindOf(data);
  const text = kindText(data);
  const color = game.color;
  const [solved, setSolved] = useState<"" | "now" | "before">(solvedBefore ? "before" : "");
  const [wrong, setWrong] = useState<WrongInfo | null>(null);
  const [hinted, setHinted] = useState(0);

  // A blunder accepts anything that holds; an opening puzzle accepts only
  // what is close enough to count as theory, so a refuted book move that lost
  // a little is still shown as the mistake it was.
  const answerTolerance = kind === "opening" ? OPENING_ANSWER_TOLERANCE : (tolerance ?? 0.15);
  const title = `${text.label}: ${text.prompt}`;
  const [puzzle, snapshot] = usePuzzle(
    {
      fen: data.fen,
      moves: data.widget_moves,
      refutations: data.refutations || [],
      epilogues: data.epilogues || [],
      answerCheck: { best: data.best_score, tolerance: answerTolerance },
      // Once a puzzle is over the board stays live: step back into the line
      // and play something else, and the engine answers it.
      explore: true,
    },
    {
      onMove: (move) => {
        // A move that lands is a new attempt: what was said about the last
        // one no longer applies, and a hint starts over.
        if (move.by === "you" && move.correct) setWrong(null);
        setHinted(0);
      },
      onWrong: (info) => setWrong(info),
      onSolved: () => {
        setWrong(null);
        setSolved((was) => was || "now");
        onSolved?.();
      },
      onFault: (f) => reportFault(f.kind, f.detail, { source: "board", fen: f.fen, moves: f.moves, board: title }),
      askEngine: engineLine,
      askScore: scoreOf,
    }
  );

  function pressHint() {
    const next = (hinted + 1) % HINT_STEPS.length;
    setHinted(next);
    puzzle.hint(next === 1 ? "square" : next === 2 ? "arrow" : "clear");
  }

  const retrying = snapshot.phase === "punishing" || snapshot.phase === "punished";
  // Solved the moment the last move lands, not once the line after it has
  // played out: Next is what the finger wants next.
  const over = solved !== "";
  const hintSlot = retrying ? (
    <button
      type="button"
      className="btn btn-primary hint-btn puzzle-retry"
      onClick={() => {
        puzzle.retry();
        setWrong(null);
      }}
    >
      <Icon name="stepBack" />
      Retry
    </button>
  ) : (
    <button
      type="button"
      className={"btn hint-btn puzzle-hint" + (over ? " spent" : "")}
      title={HINT_STEPS[hinted]!.title}
      disabled={!snapshot.hintable && hinted === 0}
      onClick={pressHint}
    >
      <Icon name="eye" />
      {HINT_STEPS[hinted]!.label}
    </button>
  );

  return (
    <BoardScreen
      className={"puzzle-screen" + (over ? " solved" : "")}
      hidden={hidden}
      board={<PuzzleBoard puzzle={puzzle} snapshot={snapshot} boardStyle={prefs.board} pieces={prefs.pieces} />}
      line={<LineRow puzzle={puzzle} snapshot={snapshot} />}
      bar={
        <>
          {lead}
          {hintSlot}
          {slots(over)}
          <BoardMenu
            shown={() => ({
              ...puzzle.shown(),
              headers: {
                Event: game.event || "Blundermine",
                Site: location.href,
                Date: pgnDate(game.played_at),
                White: game.white,
                Black: game.black,
                Result: game.result,
              },
            })}
          />
        </>
      }
      sheet={
        <>
          {sheetTop}
          <div className="puzzle-badges">
            <span className={"badge " + kind}>{KIND[kind].label}</span>
            {kind === "opening" && (data.opening?.position_name || data.opening?.name) ? (
              <span className="puzzle-opening-name">{data.opening.position_name || data.opening.name}</span>
            ) : null}
            {data.difficulty && data.difficulty > 0 ? (
              <span className="badge neutral num" title="Estimated difficulty">
                ~{data.difficulty}
              </span>
            ) : null}
            {solved ? (
              <span className="badge solved">
                <Icon name="check" />
                {solved === "before" ? "Solved before" : "Solved"}
              </span>
            ) : null}
          </div>
          <h2 className="puzzle-title">
            You played {moveLabel(data.move_number, color)} <span className="san">{data.played_san}</span>
          </h2>
          <Feedback wrong={wrong} snapshot={snapshot} />
          <Aftermath puzzle={puzzle} snapshot={snapshot} />
          <LineMoves puzzle={puzzle} snapshot={snapshot} />
          <p className="puzzle-evals" title="From White's side, as on the board: a plus is good for White whichever colour you played.">
            <b>{fmtScore(whitePov(data.best_score, color))}</b> was there · {data.played_san} left <b>{fmtScore(whitePov(data.played_score, color))}</b>
          </p>
          {kind === "opening" ? <OpeningExplain puzzle={data} color={color} /> : <p className="puzzle-why">{text.why}</p>}
          <Tags tags={data.tags || []} onTag={onTag} />
          {!snapshot.replay ? <p className="puzzle-prompt">{text.prompt}</p> : null}
          <Foot data={data} game={game} showGame={showGame} repertoireId={repertoireId} onAccepted={() => (setSolved("now"), onSolved?.())} />
          {sheetBottom}
        </>
      }
    />
  );
}

/** What a wrong move got. */
function Feedback({ wrong, snapshot }: { wrong: WrongInfo | null; snapshot: Snapshot }) {
  if (!wrong) return null;
  const punished = wrong.punished;
  return (
    <div className="puzzle-feedback" role="status" aria-live="polite">
      <div className="puzzle-feedback-text">
        <b>{wrong.san}?</b>{" "}
        {punished ? (
          wrong.phase === "playing" ? (
            "Watch what it runs into…"
          ) : (
            <>
              <span className="puzzle-feedback-line">{(wrong.line || []).join(" ")}</span>
              {wrong.eval ? (
                <>
                  {" — "}
                  <b className="num">{wrong.eval}</b> {wrong.verdict || ""}
                </>
              ) : null}
            </>
          )
        ) : (
          "Not that one. The piece went back — have another look."
        )}
      </div>
      {punished && snapshot.phase === "punished" ? <p className="faint">Retry puts the position back.</p> : null}
    </div>
  );
}

/** A tag that names a motif is the way to more of the same: pressed, it
 *  narrows Train to that motif. The ones that only say how long the answer
 *  is stay words. */
function Tags({ tags, onTag }: { tags: string[]; onTag?: (tag: string) => void }) {
  const shown = tags.filter((t) => !HIDDEN_TAGS.has(t));
  if (!shown.length) return null;
  return (
    <div className="puzzle-tags">
      {shown.map((t) =>
        TAG_MEANS[t] ? (
          <button
            key={t}
            type="button"
            className="tag tag-btn"
            title={`${TAG_MEANS[t]} Press to train only these.`}
            onClick={() => {
              prefs.trainMotif = t;
              prefs.trainKind = "";
              savePrefs();
              if (onTag) onTag(t);
              else navigate("/");
            }}
          >
            {humanTag(t)}
          </button>
        ) : (
          <span key={t} className="tag">
            {humanTag(t)}
          </span>
        )
      )}
    </div>
  );
}

function Foot({
  data,
  game,
  showGame,
  repertoireId,
  onAccepted,
}: {
  data: PuzzleData;
  game: GameInfo;
  showGame?: boolean;
  repertoireId?: number | null;
  onAccepted: () => void;
}) {
  const kind = kindOf(data);
  const color = game.color;
  const openingAsked = data.opening?.position_name || data.opening?.name || "";
  const accepting = useRef(false);
  const [accepted, setAccepted] = useState(false);
  return (
    <div className="puzzle-foot">
      {/* A repertoire deviation can be settled from here: either the move
          you played becomes part of your line, or you go and drill the line
          you left. */}
      {kind === "opening" && data.opening?.source === "repertoire" && repertoireId != null ? (
        <div className="btn-row">
          <button
            type="button"
            className="btn btn-sm"
            disabled={accepted}
            onClick={async () => {
              if (accepting.current) return;
              accepting.current = true;
              try {
                await sendJson("POST", `/api/repertoires/${repertoireId}/accept`, { epd: epdOf(data.fen), san: data.played_san });
                toast(`${data.played_san} is part of your line now.`);
                setAccepted(true);
                onAccepted();
              } catch (err) {
                if (!isSignedOut(err)) toast(errorText(err));
              } finally {
                accepting.current = false;
              }
            }}
          >
            Add {data.played_san} to my line
          </button>
          <a className="btn btn-sm" {...follow(`/openings/${repertoireId}/drill?from=${encodeURIComponent(epdOf(data.fen))}`)}>
            <Icon name="book" />
            Drill this line
          </a>
        </div>
      ) : null}
      {/* A mistake inside theory is one position of an opening, and the
          question after it is the opening. */}
      {kind === "opening" && data.opening?.source !== "repertoire" && openingAsked ? (
        <div className="btn-row">
          <a
            className="btn btn-sm"
            title={`${openingAsked}: what to play against it, what is played from it, and a course to learn it`}
            {...follow(`/openings/learn?line=${encodeURIComponent(openingAsked)}&as=${color}`)}
          >
            <Icon name="book" />
            Study this opening
          </a>
        </div>
      ) : null}
      {showGame ? (
        <div className="puzzle-game">
          <span>vs {opponentOf(game)}</span>
          <span>{speedLabel(game)}</span>
          {game.played_at ? <span>{fmtDate(game.played_at)}</span> : null}
          {game.game_id ? <a {...follow(`/games/${game.game_id}/${color}?ply=${data.ply}`)}>Open game</a> : null}
        </div>
      ) : null}
      {game.game_id ? (
        <a className="faint puzzle-lichess" href={`https://lichess.org/${game.game_id}/${color}#${data.ply}`} target="_blank" rel="noopener">
          This position on Lichess
          <Icon name="ext" />
        </a>
      ) : null}
    </div>
  );
}
